import { ref, onMounted } from 'vue'
import axios from 'axios'

const currentVersion = ref<string | null>(null)
const latestVersion = ref<string | null>(null)
const hasUpdate = ref(false)
const checking = ref(false)

// Store version in localStorage
const STORAGE_KEY = 'app_version'

export function useAppVersion() {
  /**
   * Get current stored version
   */
  const getCurrentVersion = (): string | null => {
    return localStorage.getItem(STORAGE_KEY)
  }

  /**
   * Store new version
   */
  const setCurrentVersion = (version: string) => {
    localStorage.setItem(STORAGE_KEY, version)
    currentVersion.value = version
  }

  /**
   * Check for updates from server
   */
  const checkForUpdates = async (): Promise<boolean> => {
    checking.value = true
    try {
      const response = await axios.get('/backend/api/version.php', {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      })

      if (response.data.success) {
        latestVersion.value = response.data.data.version
        const stored = getCurrentVersion()

        if (stored && stored !== latestVersion.value) {
          hasUpdate.value = true
          return true
        } else if (!stored && latestVersion.value) {
          // First time, just store it
          setCurrentVersion(latestVersion.value)
        }
      }
      return false
    } catch (error) {
      console.error('Failed to check app version:', error)
      return false
    } finally {
      checking.value = false
    }
  }

  /**
   * Force reload to update app
   */
  const updateApp = () => {
    if (latestVersion.value) {
      setCurrentVersion(latestVersion.value)
    }
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name))
      })
    }
    window.location.reload()
  }

  /**
   * Dismiss update notification
   */
  const dismissUpdate = () => {
    hasUpdate.value = false
  }

  // Initialize on mount
  onMounted(() => {
    currentVersion.value = getCurrentVersion()
  })

  return {
    currentVersion,
    latestVersion,
    hasUpdate,
    checking,
    checkForUpdates,
    updateApp,
    dismissUpdate,
  }
}
